import { useCurrentUserAccount, useUserAccounts } from '@services/state';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import './style.scss';

/**
 * What the new owner sees immediately after claiming the account.
 *
 * It says two things and separates them carefully: the company is theirs now, and the paid
 * features are still off. Claiming an unclaimed account is free and instant; a plan is a purchase
 * and has not happened. Blurring those would have someone believe they had bought something.
 *
 * Both onward paths are offered without pressure. Starting with the assigned project is a
 * perfectly good answer for someone who came here to estimate one job.
 */
export const OwnershipAssumed: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [account] = useCurrentUserAccount();
  const [, refreshAccounts] = useUserAccounts();

  /*
   * Read the account again on arrival.
   *
   * The claim screen already refreshes before it sends anyone here, so in the ordinary flow this
   * is a no-op. It is here for the ways people reach this page that skip that: a reload, a
   * restored tab, a bookmark. Each onward button from here opens a screen whose contents depend
   * on the account being claimed, and arriving at one of those with a stale shell in the store is
   * the failure this page exists to rule out.
   */
  React.useEffect(() => {
    refreshAccounts().catch(() => {
      // A failed refresh is not worth blocking this page over: nothing here is wrong without it,
      // and the next read of the account will correct the store anyway.
    });
  }, [refreshAccounts]);

  const firm = account?.companyName ?? t('Ownership.YourFirm');

  return (
    <div className="cc-own">
      <header className="cc-own-head">
        <span className="eyebrow">{t('Ownership.Eyebrow')}</span>
        <h1>{t('Ownership.Assumed.Title', { firm })}</h1>
        <p>{t('Ownership.Assumed.Lead')}</p>
      </header>

      <div className="cc-own-why" style={{ maxWidth: 620, margin: '0 auto 24px' }}>
        {t('Ownership.Assumed.Next')}
      </div>

      <div className="cc-own-modal-actions" style={{ justifyContent: 'center' }}>
        <button type="button" className="cc-own-secondary" onClick={() => navigate('/dashboard')}>
          {t('Ownership.Assumed.StartProject')}
        </button>
        <button type="button" className="cc-own-primary" onClick={() => navigate('/settings/subscription')}>
          {t('Ownership.Assumed.ChoosePlan')}
        </button>
      </div>
    </div>
  );
};
